import React, { useState, useEffect } from 'react';
import { Modal, Heading, Text } from './ui';
import { createPost, updateSessionReflection } from '../services/api';
import { useAuth } from '../context/authContext';

const colorTokens = {
    alabaster: '#EEEEE7',
    white: '#FFFFFF',
    oxfordBlue: '#1b263b',
    richBlack: '#0d1b2a',
    neutral: {
        0: '#FFFFFF',
        50: '#F9F8F6',
        900: '#0D0D0D'
    }
};

const PostSharingModal = ({ 
    isOpen, 
    onClose, 
    sessionId, 
    sessionReport,
    onPostCreated 
}) => {
    const { user } = useAuth();
    const [caption, setCaption] = useState('');
    const [selectedFinalImage, setSelectedFinalImage] = useState(null);
    const [selectedProgressPhotos, setSelectedProgressPhotos] = useState([]);
    const [posting, setPosting] = useState(false);
    const [error, setError] = useState(null);
    const [forkTitle, setForkTitle] = useState('');
    const [showForkTitleEdit, setShowForkTitleEdit] = useState(false);

    // Reset state when modal opens/closes
    useEffect(() => {
        if (isOpen) {
            console.log('PostSharingModal opened with sessionReport:', sessionReport);
            setCaption('');
            setSelectedFinalImage(null);
            setSelectedProgressPhotos([]);
            setError(null);
            setForkTitle('');
            setShowForkTitleEdit(false);
            
            // Auto-select final image if available
            if (sessionReport?.final_outcome?.dish_photo) {
                console.log('Setting final image:', sessionReport.final_outcome.dish_photo);
                setSelectedFinalImage(sessionReport.final_outcome.dish_photo);
            }
            
            // Auto-select progress photos from timeline, highlights, and session photos
            const progressPhotos = [];
            
            // Add photos from timeline
            if (sessionReport?.timeline) {
                sessionReport.timeline.forEach(entry => {
                    if (entry.photos && entry.photos.length > 0) {
                        entry.photos.forEach(photo => {
                            progressPhotos.push({
                                ...photo,
                                caption: entry.notes || entry.step_title || 'Progress photo'
                            });
                        });
                    }
                });
            }
            
            // Add photos from highlights
            if (sessionReport?.highlights) {
                sessionReport.highlights.forEach(highlight => {
                    if (highlight.photos && highlight.photos.length > 0) {
                        highlight.photos.forEach(photo => {
                            progressPhotos.push({
                                ...photo,
                                caption: highlight.description || highlight.title || 'Highlight photo'
                            });
                        });
                    }
                });
            }
            
            // Add photos from session_photos field (fallback)
            if (sessionReport?.session_photos && Array.isArray(sessionReport.session_photos)) {
                sessionReport.session_photos.forEach((photoUrl, index) => {
                    if (typeof photoUrl === 'string') {
                        progressPhotos.push({
                            url: photoUrl,
                            caption: `Session photo ${index + 1}`,
                            timestamp: new Date().toISOString(),
                            type: 'process'
                        });
                    }
                });
            }
            
            setSelectedProgressPhotos(progressPhotos);
        }
    }, [isOpen, sessionReport]);

    const handlePost = async () => {
        if (!user || !sessionId) return;
        
        setPosting(true);
        setError(null);
        
        try {
            const token = await user.getIdToken();
            
            // Update fork title if provided
            if (sessionReport?.fork_id && forkTitle.trim()) {
                try {
                    const { updateRecipe } = await import('../services/api');
                    await updateRecipe(sessionReport.fork_id, {
                        name: forkTitle.trim()
                    }, token);
                    console.log('Fork title updated successfully');
                } catch (titleError) {
                    console.error('Error updating fork title:', titleError);
                    // Don't fail the post creation if title update fails
                }
            }
            
            const postData = {
                session_id: sessionId,
                caption: caption.trim(),
                final_image: selectedFinalImage,
                progress_photos: selectedProgressPhotos
            };
            
            // First, submit the reflection data to update the fork's photo
            try {
                console.log('Submitting reflection data for session:', sessionId);
                const reflectionData = {
                    session_photos: selectedProgressPhotos.map(photo => photo.url),
                    final_dish_photo: selectedFinalImage?.url,
                    reflection_comments: caption.trim() || null,
                    reflection_ingredient_notes: null,
                    reflection_improvement_notes: null
                };
                
                console.log('Reflection data:', reflectionData);
                await updateSessionReflection(sessionId, reflectionData, token);
                console.log('Reflection submitted successfully');
            } catch (reflectionError) {
                console.error('Error submitting reflection:', reflectionError);
                // Don't fail the post creation if reflection submission fails
            }
            
            console.log('Creating post with data:', postData);
            const createdPost = await createPost(postData, token);
            console.log('Post created successfully:', createdPost);
            
            if (onPostCreated) {
                onPostCreated(createdPost);
            }
            
            onClose();
        } catch (err) {
            console.error('Error creating post:', err);
            setError(err.message || 'Failed to create post');
        } finally {
            setPosting(false);
        }
    };

    const handleSkip = () => {
        // Call onPostCreated with null to indicate skipping
        if (onPostCreated) {
            onPostCreated(null);
        }
        onClose();
    };

    const toggleProgressPhoto = (photo) => {
        setSelectedProgressPhotos(prev => {
            const isSelected = prev.some(p => p.url === photo.url);
            if (isSelected) {
                return prev.filter(p => p.url !== photo.url);
            } else {
                return [...prev, photo];
            }
        });
    };

    const updateProgressPhotoCaption = (photoUrl, newCaption) => {
        setSelectedProgressPhotos(prev => 
            prev.map(photo => 
                photo.url === photoUrl 
                    ? { ...photo, caption: newCaption }
                    : photo
            )
        );
    };

    if (!isOpen) return null;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Share Your Meal"
            maxWidth="max-w-4xl"
        >
            <div className="space-y-6">
                {/* Header */}
                <div className="text-center">
                    <Heading level={3} className="text-xl font-medium mb-2">
                        Share your cooking journey with friends!
                    </Heading>
                    <Text className="text-sm opacity-60">
                        Your final dish and progress photos will be shared on The Table
                    </Text>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="bg-red-50 border border-red-200 rounded p-3">
                        <Text className="text-red-600 text-sm">{error}</Text>
                    </div>
                )}

                {/* Caption */}
                <div>
                    <Text className="font-medium mb-2">Add a caption (optional)</Text>
                    <textarea
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        placeholder="Share your thoughts about this cooking session..."
                        className="w-full p-3 border border-neutral-300 rounded text-sm text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        rows={3}
                    />
                </div>

                {/* Fork Title Edit - Only show if this session created a fork */}
                {sessionReport?.fork_id && (
                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <Text className="font-medium">Recipe Title</Text>
                            <button
                                onClick={() => setShowForkTitleEdit(!showForkTitleEdit)}
                                className="px-3 py-1 text-xs font-mono uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                            >
                                {showForkTitleEdit ? 'Cancel' : 'Edit Title'}
                            </button>
                        </div>
                        
                        {showForkTitleEdit ? (
                            <div className="space-y-3">
                                <input
                                    type="text"
                                    value={forkTitle}
                                    onChange={(e) => setForkTitle(e.target.value)}
                                    placeholder="Enter a new title for your fork..."
                                    className="w-full p-3 border border-neutral-300 rounded text-sm text-neutral-900 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                                <Text className="text-xs opacity-60">
                                    This will update the title of your forked recipe
                                </Text>
                            </div>
                        ) : (
                            <div className="p-3 bg-neutral-50 hairline">
                                <Text className="text-sm text-neutral-900">
                                    {sessionReport.recipe_title || 'Untitled Recipe'}
                                </Text>
                            </div>
                        )}
                    </div>
                )}

                {/* Final Image */}
                {sessionReport?.final_outcome?.dish_photo && (
                    <div>
                        <Text className="font-medium mb-3">Final Dish Photo</Text>
                        <div className="flex items-start gap-4">
                            <img
                                src={sessionReport.final_outcome.dish_photo.url}
                                alt="Final dish"
                                className="w-32 h-32 object-cover hairline aspect-square"
                            />
                            <div className="flex-1">
                                <Text className="text-sm opacity-60">
                                    This will be the main photo shown in your post
                                </Text>
                            </div>
                        </div>
                    </div>
                )}

                {/* Progress Photos */}
                <div>
                    <div className="flex items-center justify-between mb-3">
                        <Text className="font-medium">Progress Photos</Text>
                        <Text className="text-sm font-mono uppercase tracking-wider opacity-60">
                            {selectedProgressPhotos.length} Selected
                        </Text>
                    </div>
                    <Text className="text-sm opacity-60 mb-4">
                        Select which progress photos to include in your post
                    </Text>
                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {selectedProgressPhotos.map((photo, index) => {
                            const isSelected = selectedProgressPhotos.some(p => p.url === photo.url);
                            return (
                                <div key={index} className="space-y-2">
                                    <div 
                                        className="relative cursor-pointer group"
                                        onClick={() => toggleProgressPhoto(photo)}
                                    >
                                        <img
                                            src={photo.url}
                                            alt={`Progress ${index + 1}`}
                                            className="w-full aspect-square object-cover hairline"
                                        />
                                        <div className={`absolute top-2 right-2 w-7 h-7 flex items-center justify-center text-sm font-mono transition-all ${
                                            isSelected
                                                ? 'text-white'
                                                : 'bg-white text-neutral-900 hairline group-hover:bg-neutral-100'
                                        }`}
                                        style={{ 
                                            backgroundColor: isSelected ? colorTokens.oxfordBlue : undefined
                                        }}>
                                            {isSelected ? '✓' : '+'}
                                        </div>
                                    </div>
                                    {isSelected && (
                                        <input
                                            type="text"
                                            value={photo.caption || ''}
                                            onChange={(e) => updateProgressPhotoCaption(photo.url, e.target.value)}
                                            placeholder="Add caption..."
                                            className="w-full p-2 text-xs hairline bg-white focus:outline-none focus:ring-2"
                                            style={{ focusRingColor: colorTokens.oxfordBlue }}
                                        />
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    
                    {selectedProgressPhotos.length === 0 && (
                        <div className="text-center py-8">
                            <Text className="opacity-60">No progress photos available from this session</Text>
                        </div>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 justify-between pt-4 border-t border-neutral-200">
                    <button
                        onClick={handleSkip}
                        className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                        disabled={posting}
                    >
                        Skip Posting
                    </button>
                    <div className="flex gap-4">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 font-mono text-sm uppercase tracking-wider bg-neutral-50 text-neutral-900 hairline hover:bg-neutral-900 hover:text-neutral-0 transition-colors"
                            disabled={posting}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handlePost}
                            className="px-4 py-2 font-mono text-sm uppercase tracking-wider text-white hover:opacity-60 transition-opacity"
                            style={{ backgroundColor: colorTokens.oxfordBlue }}
                            disabled={posting}
                        >
                            {posting ? 'Posting...' : 'Share Post'}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default PostSharingModal;
